import { inject, Injectable } from "@angular/core";
import { KpiStateStoreService } from "./KPI-state-stores/kpi-state-store.service";
import { FieldTypes, IAdditionalInfo, IBizEntity, IBizEntityType, IFieldMeta, IRelationshipConfiguration, Unit } from "@optima-stride-ai/core";
import { UserContextService } from "@optima-stride-ai/optima-platform";
import { IKPI } from "./kpi.model";
import { DateRange } from "../../models/interfaces/date-range-model";
import { faker } from "@faker-js/faker";
import { getEmptyBizEntity } from "../utils/form/function-init-form-state";
import { MeasuresStateStoreService } from "../transaction(Measure)/measure-state-store/measures-state-store.service";
import { ConnectionPollingModes, IMeasure, IMeasureLog, IMeasureSource, KPIAndMeasureCategories, MeasureSources } from "../transaction(Measure)/measure.model";


export const DEFAULT_KPI_BIZ_RULES = {
    "selection": [
        {
            "fact": {
                "columnName": "datetime",
                "columnAlias": "date",
                "isTableColumn": true,
                "isReference": false
            },
            "columnType": "timestamp"
        },
        {
            "fact": {
                "columnName": "status",
                "columnAlias": "status",
                "isTableColumn": false,
                "isReference": false
            },
            "columnType": "number"
        }
    ],
    "orderBy": [
        {
            "columnAlias": "date",
            "orderType": "DESC"
        }
    ],
    "filter": null,
    "limit": null,
    "statusFieldName": 'status'
};


@Injectable({
    providedIn: 'root'
})
export class AutoCreateEntitiesService {
    private kpiStateStoreService: KpiStateStoreService = inject(KpiStateStoreService);
    private measureStateStoreService: MeasuresStateStoreService = inject(MeasuresStateStoreService);


    private userContextService = inject(UserContextService);

    constructor() {
    }

    fieldValueKey = 'status';

    createKPIAutomatically(
        kpiType: IBizEntityType,
        measureType:IBizEntityType,
        kpiParentId: string,
        measureParentId: string,
        name: string,
        ownerId: any, // ask @mudit-OptimaStride earlier it was IUser
        category: KPIAndMeasureCategories,
        unit?: Unit,
        dateRange?: DateRange
    ): Promise<IKPI> {
        let icon = 'cilSpeedometer';
        switch (category) {
            case KPIAndMeasureCategories.Performance:
                icon = 'cilGraph';
                break;
            case KPIAndMeasureCategories.Quality:
                icon = 'cilCheck';
                break;
            case KPIAndMeasureCategories.Innovation:
                icon = 'cilLightbulb';
                break;
            default:
                icon = 'cilSpeedometer';
        }
    
        let additionalInfo: IAdditionalInfo[] = [];
    
        // Use provided date range or default to ±1 year from current date
        if (!dateRange) {
            const minDate = new Date();
            minDate.setFullYear(minDate.getFullYear() - 1);
            const maxDate = new Date();
            maxDate.setFullYear(maxDate.getFullYear() + 1);
            dateRange = new DateRange(minDate, maxDate);
        }
    
        kpiType.additionalFields.forEach((field) => {
            additionalInfo.push({
                key: field.name,
                value: '',
                fieldMeta: field
            });
        });
    
        // Create KPI only after measure is created
        return this.createMeasureAutomatically(measureType, measureParentId, name, ownerId, category)
            .then((measure: any) => {
              return this.measureStateStoreService.getState().then((measures) =>{
                if(measures)
                {
                  measure = measures.find( m => m.id === measure.biz_entity_id);
                let kpi: IKPI = {
                  type: kpiType,
                  id: '1',
                  parentId: kpiParentId,
                  tenantId: '1' ,//ask @mudit-OptimaStride tenant.AcmeInc.toString(),
                  appId: '1',
                  name: `Auto-generated ${kpiType.name} for ${name}`,
                  shortName: name,
                  description: `Auto-generated ${kpiType.name} for ${name}`,
                  icon: icon,
                  ownerId: Number(ownerId) || Number(this.userContextService.userUUID),
                  draft: false,
                  unit: unit,
                  // attachedPlugins: [],
                  additionalInfo: additionalInfo,
                  archived: false,
                  archivedBy: null,
                  archivedOn: null,
                  createdOn: new Date(),
                  createdBy: null,
                  modifiedOn: null,
                  modifiedBy: null,
                  teamIds: [],
                  progressData: null,
                  labels: [],
                  // ownerDetail: ownerDetail,
                  status: [],
                  engagement: [],
                  validForDuration: dateRange,
                  measureId: measure.id!,
                  projectedProgression: [],
                  bizRules: DEFAULT_KPI_BIZ_RULES,
                  statusFieldName: this.fieldValueKey,
                  category: category,
                  measure: [measure!]
                  };
                  return this.kpiStateStoreService.create(kpi);
                }
                else{
                  return Promise.reject(new Error('Failed to create KPI'));
                }
              })
                
            });
    }

    createMeasureAutomatically(
      type : IBizEntityType,
      parentId: string,
      name: string,
      ownerId: any, // ask @mudit-OptimaStride earlier it was IUser
      category: KPIAndMeasureCategories
    ) :Promise<IMeasure> {
      let icon = 'cilChartLine';
      let additionalInfo: IAdditionalInfo[] = [];
    
      type.additionalFields.forEach((field) => {
        additionalInfo.push({
            key:'',
            value:'',
            fieldMeta:field
        })
      }); 
    
      let measureLogFieldMeta: IFieldMeta[] = [{
        name: this.fieldValueKey,
        label:'Status',
        type:FieldTypes.number,
        viewInList:true,
        isRequired:true,
      }];
      
      let group = 'Retrospective';
      let subGroup = 'Tickets';
    
      const measureSource: IMeasureSource = {
        connection:{
          id:faker.string.uuid(),
          tenantId:"1" ,//ask @mudit-OptimaStride tenant.AcmeInc.toString()
          appId:'1',
          source:MeasureSources.MANUAL,
          name:'Manual',
          connectionConfig:{},
          pollingMode: ConnectionPollingModes.POLL,
          pollingFrequencySeconds: 300,
        },
        source: MeasureSources.MANUAL,
        apiEndpoint:'',
      }
      let measure = {
        type: type,
        id: '1',
        parentId: parentId,
        tenantId: "1", //ask @mudit-OptimaStride tenant.AcmeInc.toString(),
        appId: '1',
        name: ` Auto generated ${type.name} for ${name}`,
        shortName: name,
        description: ` Auto generated ${type.name} for ${name}`,
        icon: icon,
        ownerId: Number(ownerId) || Number(this.userContextService.userUUID),
        draft: false,
        attachedPlugins: [],
        additionalInfo: additionalInfo,
        archived: false,
        archivedBy: null,
        archivedOn: null,
        createdOn: new Date(),
        createdBy: null,
        modifiedOn: null,
        modifiedBy: null,
        teamIds: [],
        progressData: null,
        labels: [],
        // ownerDetail: ownerDetail,
        status: [],
        engagement: [],
        group: group,
        subGroup: subGroup,
        source: measureSource,
        category: category,
        logFieldsMeta: measureLogFieldMeta
      }
    //   return measure;
      const result = this.measureStateStoreService.create(measure);
      if (result) {
        return result as Promise<IMeasure>;
      } else {
        return Promise.reject(new Error('Failed to create measure'));
      }
    }

    createBizEntityAutomaticallyFromBizEntity(existingBizEntity: IBizEntity, name: string, additionalInfo: IAdditionalInfo[], config: IRelationshipConfiguration, entityParentId?: string): IBizEntity{
      let bizEntity = getEmptyBizEntity(config.target as IBizEntityType);
      bizEntity.id = new Date().toString();
      bizEntity.additionalInfo = additionalInfo || [];
      bizEntity.name = name ? name : existingBizEntity.name + "  " + (config.target as IBizEntityType).name;
      bizEntity.description = existingBizEntity.description + "  " + (config.target as IBizEntityType).description;
      bizEntity.icon = existingBizEntity.icon;
      bizEntity.parentId = entityParentId || (existingBizEntity?.parentId?.toString() ?? '');
      bizEntity.ownerId = existingBizEntity.ownerId || '';
      bizEntity.createdOn = new Date();
      bizEntity.modifiedOn = new Date();

      return bizEntity;
    }
}

export function createMeasureLogJSON(key:string, statusValue:string, measure:IMeasure, unit?:Unit): any {
  const logFieldMeta: IAdditionalInfo[] = []

  measure.logFieldsMeta.forEach((field) => {
    if(field.name === key)
    {
      logFieldMeta.push({
        key:field.name,
        value:statusValue,
        fieldMeta:field
      })
    }
    else if(field.name === 'unit')
    {
      logFieldMeta.push({
        key:field.name,
        value:unit?.symbol || '',
        fieldMeta:field
      })
    }
    else
    {
      logFieldMeta.push({
          key:field.name,
          value:'',
          fieldMeta:field
      })
    }
  });

 

  const measureLog: IMeasureLog = {
    "id": faker.number.int(),
    "tenantId": "0001",
    "appId": "0001",
    "date": new Date(), //TBD Replace Hard coded date with current date
    "measure": measure,
    "logFieldValues": logFieldMeta,
    "author": {
      "type": {
        "id": "c3f14b77-0af2-4a69-80b3-0b1d9f35b34e",
        "tenantId": "0001",
        "appId": "0001",
        "name": "User",
        "description": "Commodi tyrannus creber in.",
        "allowedTypes": [],
        "icon": "cil-user",
        "displayOrder": 15,
        "isApplicationDataNode": false,
        "systemAllowedTypes": [],
        "isSystemType": false,
        "category": 1,
        "isRootNode": false,
        "teamRequired": false,
        "enableChat": false,
        "allowRiskManagement": false,
        "allowAttachments": false,
        "enableWiki": false,
        "enableTags": false,
        "functionType": 0,
        "additionalFields": [
          {
            "name": "FLD_001",
            "label": "Ducimus",
            "type": FieldTypes.number,
            "isFilterable": true,
            "isSearchable": true,
            "icon": "cilBook",
            "min": 0,
            "max": 0,
            "validationMessages": [],
            "inputPattern": ".*",
            "hiddenInForm": false
          },
          {
            "name": "FLD_002",
            "label": "Cimentarius",
            "type": FieldTypes.text,
            "isFilterable": true,
            "isSearchable": true,
            "icon": "cilPeople",
            "min": 0,
            "max": 100,
            "validationMessages": [],
            "inputPattern": ".*",
            "hiddenInForm": false
          },
          {
            "name": "FLD_003",
            "label": "Vitium",
            "type": FieldTypes.text,
            "isFilterable": true,
            "isSearchable": true,
            "icon": "cilUser",
            "min": 0,
            "max": 100,
            "validationMessages": [],
            "inputPattern": ".*",
            "hiddenInForm": false
          },
          {
            "name": "FLD_004",
            "label": "Aranea",
            "type": FieldTypes.date,
            "isFilterable": true,
            "isSearchable": true,
            "icon": "cilCheckCircle",
            "min": 0,
            "max": 0,
            "validationMessages": [],
            "inputPattern": ".*",
            "hiddenInForm": false
          }
        ],
        "isAlwaysNavigable": false
      },
      "id": "",
      "parentId": null,
      "tenantId": "",
      "appId": "",
      "name": "",
      "shortName": "",
      "first_name":"",
      "last_name":"",
      "uuid": "",
      "description": "",
      "icon": "",
      "ownerId": 0,
      "draft": false,
      "attachedPlugins": [],
      "additionalInfo": [],
      "archived": false,
      "archivedBy": null,
      "archivedOn": null,
      "createdBy": 61,
      "createdOn": new Date(),
      "modifiedBy": null,
      "designation": "",
      "email": "",
      "is_admin": false,
      "registered": true,
      "avatar": "",
      "address": {
        "type": "",
        "building": "",
        "street1": "",
        "street2": "",
        "city": "",
        "state": "",
        "zip": "",
        "country": "",
        "countryISO": "",
        "lat": 87.750956,
        "long": -52.616823
      },
      "state": "active"
    },
    "draft": false,
    "deleted": false,
    "deletedOn": null,
    "archived": false,
    "archivedBy": null,
    "archivedOn": null,
    "modifiedOn": new Date(),
  } ;
  return measureLog;
}